import { Card, DatePicker, Result, Row, Space, Spin, Table, Tag } from 'antd';
import classNames from 'classnames/bind';
import { ResponsiveBar } from '@nivo/bar';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeadingH1 } from 'components/ui';
import API from 'helpers/API';
import { getReclaimStatus } from 'utils/CodeMapper';
import { getDisplayName } from 'utils/getDisplayName';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import styles from './reclaims.module.scss';
import {
  buildReclaimChartData,
  buildReclaimStatusSummary,
  CUSTOMER_RECLAIM_STATUS_CARDS,
  filterReclaimsByDate,
  getRecentReclaims,
} from './reclaims.helpers';

dayjs.extend(isSameOrBefore);

const cx = classNames.bind(styles);

const Page = () => {
  const navigate = useNavigate();
  const [reclaims, setReclaims] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState();
  const [dates, setDates] = useState({
    from: dayjs().startOf('month'),
    to: dayjs(),
  });
  const [selectedDate, setSelectedDate] = useState();

  useEffect(() => {
    let isMounted = true;

    const fetchReclaims = async () => {
      setIsLoading(true);
      setFetchError(undefined);

      const response = await API.get('/customer/reclaims/status');

      if (!isMounted) return;

      if (response?.success) {
        setReclaims(response.data?.reclaims ?? []);
      } else {
        setReclaims([]);
        setFetchError(
          getApiErrorMessage(response, '회수 현황을 불러오지 못했습니다.'),
        );
      }

      setIsLoading(false);
    };

    fetchReclaims();

    return () => {
      isMounted = false;
    };
  }, []);

  const summary = useMemo(() => buildReclaimStatusSummary(reclaims), [reclaims]);
  const recentReclaims = useMemo(() => getRecentReclaims(reclaims, 10), [reclaims]);
  const chartData = useMemo(
    () => buildReclaimChartData(reclaims, dates.from, dates.to),
    [reclaims, dates],
  );
  const selectedReclaims = useMemo(
    () => filterReclaimsByDate(reclaims, selectedDate),
    [reclaims, selectedDate],
  );

  const recentColumns = [
    {
      title: '회수 요청일',
      dataIndex: 'reclaimDate',
      width: 120,
      align: 'center',
      render: (value) =>
        value ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : '-',
    },
    {
      title: '등록일시',
      dataIndex: 'createdAt',
      width: 180,
      align: 'center',
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value) => <Tag>{getReclaimStatus(value)}</Tag>,
    },
    {
      title: '요청자명',
      dataIndex: 'createdBy',
      width: 120,
      render: (_, record) => getDisplayName(record.createdBy),
    },
    {
      title: '회수 주소',
      render: (_, record) =>
        `${record.customerAddress} ${record.customerAddressDetail ?? ''}`,
    },
  ];

  const detailColumns = [
    {
      title: '상태',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value) => getReclaimStatus(value),
    },
    {
      title: '회수 요청일',
      dataIndex: 'reclaimDate',
      width: 120,
      align: 'center',
      render: (value) =>
        value ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : '-',
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '요청자명',
      dataIndex: 'createdBy',
      width: 120,
      render: (_, record) => getDisplayName(record.createdBy),
    },
    {
      title: '주소',
      render: (_, record) =>
        `${record.customerAddress} ${record.customerAddressDetail ?? ''}`,
    },
  ];

  if (isLoading) {
    return (
      <Space
        direction="vertical"
        align="center"
        size={16}
        style={{ width: '100%', padding: '80px 0' }}
      >
        <Spin size="large" />
      </Space>
    );
  }

  if (fetchError) {
    return (
      <Result
        status="error"
        title="회수 현황을 불러오지 못했습니다."
        subTitle={fetchError}
      />
    );
  }

  return (
    <div className={cx('container')}>
      <HeadingH1 title="회수 현황" />
      <Row align="middle" justify="space-between">
        <p className={cx('time')}>{dayjs().format('YYYY년 M월 D일 HH:mm 기준')}</p>
      </Row>

      <div className={cx('cards')}>
        {CUSTOMER_RECLAIM_STATUS_CARDS.map((card) => (
          <Card key={card.key} size="small" className={cx('card')}>
            <div className={cx('cardTitle')}>{card.title}</div>
            <div className={cx('cardValue')}>{summary[card.key] ?? 0}</div>
          </Card>
        ))}
      </div>

      <div className={cx('panel')}>
        <div className={cx('panelTitle')}>최근 회수 10건</div>
        <Table
          style={{ marginTop: 16 }}
          rowKey="rid"
          columns={recentColumns}
          dataSource={recentReclaims}
          pagination={false}
          onRow={(record) => ({
            onClick: () => navigate(`/customer/reclaims/${record.rid}`),
          })}
        />
      </div>

      <div className={cx('panel')}>
        <Row align="middle" justify="space-between">
          <div className={cx('panelTitle')}>일자별 회수 수</div>
          <Space>
            <DatePicker
              allowClear={false}
              value={dates.from}
              onChange={(date) => {
                setSelectedDate(undefined);
                if (date) setDates((prev) => ({ ...prev, from: date }));
              }}
            />
            ~
            <DatePicker
              allowClear={false}
              value={dates.to}
              onChange={(date) => {
                setSelectedDate(undefined);
                if (date) setDates((prev) => ({ ...prev, to: date }));
              }}
            />
          </Space>
        </Row>

        <div className={cx('chartWrapper')}>
          <ResponsiveBar
            data={chartData}
            keys={['count']}
            indexBy="label"
            margin={{ top: 40, right: 40, bottom: 64, left: 64 }}
            padding={0.4}
            colors={{ scheme: 'set1' }}
            labelTextColor="white"
            valueScale={{ type: 'linear' }}
            axisLeft={{
              legend: '회수 수',
              legendPosition: 'middle',
              legendOffset: -40,
              tickSize: 1,
            }}
            axisBottom={{
              legend: '일자',
              legendPosition: 'middle',
              legendOffset: 40,
            }}
            onClick={(event) => setSelectedDate(event.data.date)}
          />
        </div>

        {selectedDate && (
          <>
            <div className={cx('panelTitle')} style={{ marginTop: 24 }}>
              {dayjs(selectedDate).format('M월 D일')} 회수내역
            </div>
            <Table
              style={{ marginTop: 16 }}
              rowKey="rid"
              columns={detailColumns}
              dataSource={selectedReclaims}
              pagination={false}
              onRow={(record) => ({
                onClick: () => navigate(`/customer/reclaims/${record.rid}`),
              })}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Page;
